import React, { useState } from "react";

//UI Material
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  SwipeableDrawer,
  TextField,
} from "@mui/material";

//Local components
import Form from "../basics/Form";

//Helpers
import {
  typographyForMainTitles,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";

//Material icons
import { Upload } from "@mui/icons-material";
import Selector from "../basics/Selector";
import { fetchWithTokenJSONPost } from "../../Helpers/network";
import ModalWindow from "../Modal";

//Arrays
const connectorTypes = ["SAE-J1772", "Tipo 2", "GB/T"];
const powerKwValues = ["7.6", "9.6", "11"];
const powerConfigValues = ["L2", "Dual"];
const powerCordConnectorValues = ["NEMA", "Sin NEMA", "Otro"];
const tsjInterfaceValues = ["Agujero detrás", "Estándar", "EMT"];
const booleans = ["Si", "No"];

export default function EditOrderDrawer({
  open,
  setOpen,
  data,
  orderNumber,
  getData,
}) {
  //AditionalData of the first product in the order
  const product = data.products[0].aditionalData;

  // ==========> Component States <==========
  //State for new data
  const [currentData, setCurrentData] = useState();
  //State for power Kw
  const [powerKw, setPowerKw] = useState(
    powerKwValues.indexOf(product.powerKw)
  );
  //State for connector type
  const [connectorType, setConnectorType] = useState(
    connectorTypes.indexOf(product.connectorType)
  );
  //State for power config
  const [powerConfigType, setPowerConfigType] = useState(
    powerConfigValues.indexOf(product.powerConfigType)
  );
  //State for power cord connector
  const [powerCordConnector, setPowerCordConnector] = useState(
    powerCordConnectorValues.indexOf(product.powerCordConnector)
  );
  //State for pedestal
  const [pedestal, setPedestal] = useState(booleans.indexOf(product.pedestal));
  //State for company
  const [company, setCompany] = useState(product.company);
  //State for connector cable length in meters
  const [connectorCableLengthInMeters, setConnectorCableLengthInMeters] =
    useState(product.connectorCableLengthInMeters);
  //State for tsj cable length in meters
  const [tsjCableLengthInMeters, setTsjCableLengthInMeters] = useState(
    product.tsjCableLengthInMeters
  );
  //State for ethernet
  const [ethernet, setEthernet] = useState(booleans.indexOf(product.ethernet));
  //State for datacard
  const [datacard, setDatacard] = useState(booleans.indexOf(product.datacard));
  //State for wallkit
  const [wallkit, setWallkit] = useState(booleans.indexOf(product.wallkit));
  //State for TSJ interface
  const [tsjInterface, setTsjInterface] = useState(
    tsjInterfaceValues.indexOf(product.tsjInterface)
  );
  //State for RFID cards quantity
  const [rfidCardsQuantity, setRfidCardsQuantity] = useState(
    product.rfidCardsQuantity
  );
  //State for loading request
  const [isLoading, setIsLoading] = useState(false);
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");
  //Client and dueDate
  const editOrderForm = [
    {
      selection: "textfield",
      id: "client",
      initialValue: data.client,
      xs: 12,
      md: 3,
      label: "Nombre del cliente",
      multiline: false,
      type: "text",
      inputProps: {},
      placeholder: "Nombre del cliente",
      content: [],
      style: {},
    },
    {
      selection: "date_picker",
      id: "dueDate",
      initialValue: new Date(data.dueDate),
      xs: 12,
      md: 3,
      label: "Fecha de entrega",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "Fecha de entrega",
      content: [],
    },
  ];

  // ==========> Component Functions<==========
  //Function to generate aditional data
  const generateAditionalData = () => {
    const aditionalData = {
      client: currentData.client,
      dueDate: new Date(currentData.dueDate).toISOString(),
      orderNumber,
    };

    const stateMappings = [
      { state: powerKw, key: "powerKw", values: powerKwValues },
      { state: connectorType, key: "connectorType", values: connectorTypes },
      {
        state: powerConfigType,
        key: "powerConfigType",
        values: powerConfigValues,
      },
      {
        state: powerCordConnector,
        key: "powerCordConnector",
        values: powerCordConnectorValues,
      },
      { state: pedestal, key: "pedestal", values: booleans },
      { state: company, key: "company" },
      {
        state: connectorCableLengthInMeters,
        key: "connectorCableLengthInMeters",
      },
      { state: tsjCableLengthInMeters, key: "tsjCableLengthInMeters" },
      { state: ethernet, key: "ethernet", values: booleans },
      { state: datacard, key: "datacard", values: booleans },
      { state: wallkit, key: "wallkit", values: booleans },
      { state: tsjInterface, key: "tsjInterface", values: tsjInterfaceValues },
      { state: rfidCardsQuantity, key: "rfidCardsQuantity" },
    ];

    Object.entries(product).forEach(([prop, value]) => {
      const mapping = stateMappings.find(
        (mapping) => mapping.key === prop && value !== undefined
      );
      if (mapping) {
        aditionalData[mapping.key] = mapping.values
          ? mapping.values[mapping.state]
          : mapping.state;
      }
    });

    return aditionalData;
  };

  //Function to edit order
  const editOrder = async () => {
    setIsLoading(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/orders/edit-order",
        generateAditionalData()
      );
      const jsonResponse = await requestResponse.json();
      if (jsonResponse.status === "ok") {
        setModalText("Información de la orden cambiada.");
        setModalDescription(
          `Se ha cambiado correctamente la información de la orden ${orderNumber}.`
        );
      } else {
        setModalText("No se pudo cambiar la información de esta orden.");
        setModalDescription(jsonResponse.description);
      }
    } catch (error) {
      setModalText("No se pudo cambiar la información de este producto.");
      setModalDescription(
        "Por favor, verifique su conexión a internet e inténtelo nuevamente."
      );
    }
    setModalOpen(true);
    setIsLoading(false);
    getData();
  };

  // ==========> Component <==========
  return (
    <SwipeableDrawer
      anchor="bottom"
      transitionDuration={600}
      disableSwipeToOpen={true}
      open={open}
      sx={{
        zIndex: "100",
      }}
      onClose={() => setOpen(false)}
      onOpen={() => {
        /* empty arrow function */
      }}
    >
      <Grid
        container
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ padding: "15px" }}
      >
        <Grid item xs={12}>
          <Stack>
            {typographyForSubtitles("Editar orden")}
            {typographyForMainTitles(orderNumber)}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          {typographyForSubtitles("Detalles de la orden", "secondary")}
        </Grid>
        <Grid item xs={12}>
          <Form data={editOrderForm} handleStateChange={setCurrentData} />
        </Grid>
        <Grid item xs={12}>
          {typographyForSubtitles("Especificaciones del producto", "secondary")}
        </Grid>
        {product.connectorType && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Tipo de conector")}
              <Selector
                fullWidth
                value={connectorType}
                handleChange={(e) => {
                  setConnectorType(e.target.value);
                }}
                content={connectorTypes}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.powerConfigType && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Tipo de configuración")}
              <Selector
                fullWidth
                value={powerConfigType}
                handleChange={(e) => {
                  setPowerConfigType(e.target.value);
                }}
                content={powerConfigValues}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.powerKw && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Potencia (kW)")}

              <Selector
                fullWidth
                value={powerKw}
                handleChange={(e) => {
                  setPowerKw(e.target.value);
                }}
                content={powerKwValues}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.tsjInterface && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Tipo de salida TSJ")}
              <Selector
                fullWidth
                value={tsjInterface}
                handleChange={(e) => {
                  setTsjInterface(e.target.value);
                }}
                content={tsjInterfaceValues}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.powerCordConnector && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Conector de entrada")}
              <Selector
                fullWidth
                value={powerCordConnector}
                handleChange={(e) => {
                  setPowerCordConnector(e.target.value);
                }}
                content={powerCordConnectorValues}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.tsjCableLengthInMeters && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Longitud TSJ (m)")}
              <TextField
                fullWidth
                type="number"
                value={tsjCableLengthInMeters}
                onChange={(e) => {
                  setTsjCableLengthInMeters(e.target.value);
                }}
              />
            </Stack>
          </Grid>
        )}
        {product.connectorCableLengthInMeters && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Longitud Conector (m)")}
              <TextField
                fullWidth
                type="number"
                value={connectorCableLengthInMeters}
                onChange={(e) => {
                  setConnectorCableLengthInMeters(e.target.value);
                }}
              />
            </Stack>
          </Grid>
        )}
        {product.rfidCardsQuantity <= 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Cantidad RFIDs")}
              <TextField
                fullWidth
                type="number"
                value={rfidCardsQuantity}
                onChange={(e) => {
                  setRfidCardsQuantity(e.target.value);
                }}
              />
            </Stack>
          </Grid>
        )}
        {product.datacard && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Datacard")}
              <Selector
                fullWidth
                value={datacard}
                handleChange={(e) => {
                  setDatacard(e.target.value);
                }}
                content={booleans}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.ethernet && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Puerto Ethernet")}
              <Selector
                fullWidth
                value={ethernet}
                handleChange={(e) => {
                  setEthernet(e.target.value);
                }}
                content={booleans}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.wallkit && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Kit de pared")}
              <Selector
                fullWidth
                value={wallkit}
                handleChange={(e) => {
                  setWallkit(e.target.value);
                }}
                content={booleans}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        {product.pedestal && (
          <Grid item xs={6} sm={4} md={3}>
            <Stack alignItems="center">
              {typographyForText("Pedestal")}
              <Selector
                fullWidth
                value={pedestal}
                handleChange={(e) => {
                  setPedestal(e.target.value);
                }}
                content={booleans}
                width="100%"
              />
            </Stack>
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}>
          <Stack alignItems="center">
            {typographyForText("Agencia o Empresa que solicita")}
            <TextField
              fullWidth
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} align="right">
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setOpen(false)}
            >
              {"Cancelar"}
            </Button>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                sx={{ color: "white" }}
                endIcon={<Upload />}
                onClick={() => {
                  editOrder();
                }}
              >
                {"Guardar cambios"}
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
          setOpen(false);
        }}
      />
    </SwipeableDrawer>
  );
}
